import { onFCP, onLCP, onTTFB } from 'web-vitals';
import { onCLS, onINP } from 'web-vitals/attribution';
import { ReportingCenter } from '@/reporting/reportingCenter';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import {
  buildMetricPayload,
  FRONTEND_PERFORMANCE_EVENT_NAME,
  getBrowserMetricPayloadSource,
  type FrontendPerformanceMetricName,
  type MetricPayloadSource,
  type PerformanceMetricLike,
} from './metricPayload';
import { PERFORMANCE_PAGE_TYPE, resolvePerformancePageType } from './pageType';
import { startTbtMonitor, type TbtMetric, type TbtMonitorHandle } from './tbt';

export interface FrontendPerformanceWebVitals {
  onFCP: typeof onFCP;
  onLCP: typeof onLCP;
  onTTFB: typeof onTTFB;
  onCLS: typeof onCLS;
  onINP: typeof onINP;
}

export interface FrontendPerformanceMonitorOptions {
  pathname?: string;
  webVitals?: FrontendPerformanceWebVitals;
  startTbtMonitor?: typeof startTbtMonitor;
  payloadSource?: MetricPayloadSource;
}

const defaultWebVitals: FrontendPerformanceWebVitals = {
  onFCP,
  onLCP,
  onTTFB,
  onCLS,
  onINP,
};

const noopTbtMonitor: TbtMonitorHandle = {
  setFcpTime: () => undefined,
  stop: () => undefined,
};

let initialized = false;

function getCurrentPathname(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.pathname;
}

function reportMetric(
  metricName: FrontendPerformanceMetricName,
  metric: PerformanceMetricLike,
  pageType: Exclude<
    ReturnType<typeof resolvePerformancePageType>,
    typeof PERFORMANCE_PAGE_TYPE.OTHER
  >,
  payloadSource?: MetricPayloadSource,
): void {
  try {
    const properties = buildMetricPayload({
      metricName,
      metric,
      pageType,
      source: payloadSource,
    });

    ReportingCenter.getInstance().track(
      {
        eventName: FRONTEND_PERFORMANCE_EVENT_NAME,
        properties,
      },
      [ProviderType.SENSORS],
    );
  } catch (error) {
    logger.error('FrontendPerformance metric report failed:', error);
  }
}

export function initFrontendPerformanceMonitor(
  options: FrontendPerformanceMonitorOptions = {},
): void {
  if (initialized) {
    return;
  }
  initialized = true;

  try {
    const pathname = options.pathname ?? getCurrentPathname();
    const pageType = resolvePerformancePageType(pathname);

    if (pageType === PERFORMANCE_PAGE_TYPE.OTHER) {
      return;
    }

    const webVitals = options.webVitals ?? defaultWebVitals;
    const payloadSource = options.payloadSource ?? getBrowserMetricPayloadSource();
    const startTbt = options.startTbtMonitor ?? startTbtMonitor;
    let tbtMonitor = noopTbtMonitor;

    try {
      tbtMonitor = startTbt({
        report: (metric: TbtMetric) => {
          // 兼容旧看板：该 TBT 是“FCP 到页面首次隐藏”的 Long Tasks 累计值，
          // 不符合 Lighthouse 的 FCP→TTI 定义，不应与 Lighthouse TBT 对比或作为标准 TBT 使用。
          reportMetric('TBT', metric, pageType, payloadSource);
        },
      });
    } catch (error) {
      logger.error('FrontendPerformance TBT monitor start failed:', error);
    }

    registerWebVitals(webVitals, tbtMonitor, pageType, payloadSource);
  } catch (error) {
    logger.error('FrontendPerformance monitor init failed:', error);
  }
}

function registerWebVitals(
  webVitals: FrontendPerformanceWebVitals,
  tbtMonitor: TbtMonitorHandle,
  pageType: Exclude<
    ReturnType<typeof resolvePerformancePageType>,
    typeof PERFORMANCE_PAGE_TYPE.OTHER
  >,
  payloadSource?: MetricPayloadSource,
): void {
  try {
    webVitals.onFCP((metric) => {
      try {
        tbtMonitor.setFcpTime(metric.value);
      } catch (error) {
        logger.error('FrontendPerformance FCP TBT sync failed:', error);
      }

      reportMetric('FCP', metric, pageType, payloadSource);
    });
  } catch (error) {
    logger.error('FrontendPerformance FCP registration failed:', error);
  }

  try {
    webVitals.onLCP((metric) => reportMetric('LCP', metric, pageType, payloadSource));
  } catch (error) {
    logger.error('FrontendPerformance LCP registration failed:', error);
  }

  try {
    webVitals.onTTFB((metric) => reportMetric('TTFB', metric, pageType, payloadSource));
  } catch (error) {
    logger.error('FrontendPerformance TTFB registration failed:', error);
  }

  try {
    webVitals.onCLS((metric) => reportMetric('CLS', metric, pageType, payloadSource));
  } catch (error) {
    logger.error('FrontendPerformance CLS registration failed:', error);
  }

  try {
    webVitals.onINP((metric) => reportMetric('INP', metric, pageType, payloadSource));
  } catch (error) {
    logger.error('FrontendPerformance INP registration failed:', error);
  }
}

export function resetFrontendPerformanceMonitorForTest(): void {
  initialized = false;
}
